import { useState, useEffect, useRef, KeyboardEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Group, GroupMessage } from '../services/api'
import ChatBubble from '../components/ChatBubble'

export default function GroupChatPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [group, setGroup] = useState<Group | null>(null)
  const [messages, setMessages] = useState<GroupMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [showMenu, setShowMenu] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Load group info and messages
  useEffect(() => {
    if (groupId) {
      loadGroupAndMessages()
    }
  }, [groupId])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Polling for new messages
  useEffect(() => {
    if (!groupId) return

    const pollInterval = setInterval(async () => {
      try {
        const newMessages = await api.getGroupMessages(groupId)
        if (newMessages.length > messages.length) {
          setMessages(newMessages.map(msg => ({
            ...msg,
            text: msg.ciphertext, // For now, just use ciphertext as text (no E2EE for groups yet)
          })))
        }
      } catch (error) {
        console.error('Failed to poll messages:', error)
      }
    }, 3000)

    return () => clearInterval(pollInterval)
  }, [groupId, messages.length])

  const loadGroupAndMessages = async () => {
    try {
      setIsLoading(true)
      const [groupData, messagesData] = await Promise.all([
        api.getGroup(groupId!),
        api.getGroupMessages(groupId!),
      ])
      setGroup(groupData)
      setMessages(messagesData.map(msg => ({
        ...msg,
        text: msg.ciphertext, // For now, just use ciphertext as text
      })))
    } catch (error) {
      console.error('Failed to load group:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = async () => {
    const text = newMessage.trim()
    if (!text || !groupId || isSending) return

    setIsSending(true)
    setNewMessage('')

    // Create optimistic message
    const optimisticMessage: GroupMessage = {
      id: `temp-${Date.now()}`,
      senderId: user?.id || '',
      senderName: user?.username || '',
      ciphertext: text,
      timestamp: new Date().toISOString(),
      text,
    }

    setMessages(prev => [...prev, optimisticMessage])
    inputRef.current?.focus()

    try {
      const sentMessage = await api.sendGroupMessage(groupId, text)
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id
            ? { ...sentMessage, text }
            : msg
        )
      )
    } catch (error) {
      console.error('Failed to send message:', error)
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id))
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleLeaveGroup = async () => {
    if (!groupId) return
    if (!confirm('確定要離開群組嗎？')) return

    try {
      await api.leaveGroup(groupId)
      navigate('/chats')
    } catch (error) {
      console.error('Failed to leave group:', error)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="spinner border-primary border-t-primary-light" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar sticky top-0 z-10">
        <button
          onClick={() => navigate('/chats')}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Group Avatar */}
        <div className="avatar avatar-sm bg-primary-light text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>

        {/* Group Name */}
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{group?.name || '群組'}</h1>
          <p className="text-xs text-white/70">{group?.member_count || 0} 位成員</p>
        </div>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button
                onClick={() => {
                  setShowMenu(false)
                  navigate(`/group/${groupId}/info`)
                }}
                className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                群組資訊
              </button>
              <button
                onClick={() => {
                  setShowMenu(false)
                  handleLeaveGroup()
                }}
                className="w-full px-4 py-3 text-left text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                離開群組
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              開始在群組中聊天吧！
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => {
              const isSent = message.senderId === user?.id
              return (
                <div key={message.id}>
                  {/* Sender name for received messages */}
                  {!isSent && message.senderName && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 ml-2 mb-1">
                      {message.senderName}
                    </p>
                  )}
                  <ChatBubble
                    message={{
                      id: message.id,
                      senderId: message.senderId,
                      text: message.text || message.ciphertext,
                      timestamp: message.timestamp,
                      status: 'delivered',
                    }}
                    isSent={isSent}
                    formatTime={formatTime}
                  />
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Input Area */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 px-4 py-3 sticky bottom-0">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="輸入訊息..."
              rows={1}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-3xl resize-none
                         focus:outline-none focus:ring-2 focus:ring-primary/50
                         dark:text-white placeholder:text-gray-400"
              style={{ maxHeight: '120px' }}
            />
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending}
            className="p-3 bg-primary text-white rounded-full
                       hover:bg-primary-dark transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  )
}
